import { labelKey } from '@/lists/cellValue'
import type { ColumnDescriptor } from '@/lists/types'
import type { CardConflict, CardSection, EntityRecord, FieldError, FormValues } from './types'

/**
 * Логика формы карточки: что показать, что проверить, что отправить (тикет #66).
 *
 * Вынесено из компонента отдельным модулем не ради «чистоты», а потому что именно здесь
 * живут утверждения, которые способны молча соврать: какие поля уходят на сервер, какие
 * скрыты правами и что считается расхождением версий. В JSX они проверяются отрисовкой,
 * то есть косвенно.
 */

/**
 * Поля, которые карточка вправе показать.
 *
 * Источник — **описание колонок с сервера**, а не список секций: сервер не описывает
 * колонку, на которую у вызывающего нет полевого права (`EntityListService.describe`), и
 * это единственная проверка, которой можно верить. Секция перечисляет желаемое, сервер —
 * разрешённое, пересечение — показанное.
 *
 * Направление ошибки выбрано намеренно: колонка, которой нет в ответе сервера, **не
 * показывается**. Обратное прочтение — «раз про неё ничего не сказали, значит можно» —
 * это ровно тот способ показать оклад, ради запрета которого полевые права и заведены.
 */
export function visibleColumns(
  sections: CardSection[],
  descriptors: ColumnDescriptor[],
): Map<string, ColumnDescriptor> {
  const allowed = new Map(descriptors.map((column) => [column.name, column]))
  const result = new Map<string, ColumnDescriptor>()

  for (const section of sections) {
    for (const name of section.columns) {
      const descriptor = allowed.get(name)
      // Отсутствие в ответе сервера значит «нет права» либо «нет такой колонки». Клиент
      // эти два случая не различает, и различать не должен: показывать нечего в обоих.
      if (descriptor) result.set(name, descriptor)
    }
  }
  return result
}

/**
 * Секции с уже вычтенными недоступными полями.
 *
 * Секция, у которой не осталось ни одного поля, исчезает целиком: заголовок «Финансовые
 * условия» над пустотой сообщает ровно то, что мы прячем, — что раздел существует и
 * закрыт. Та же логика, что у раздела меню без доступных пунктов.
 */
export function visibleSections(
  sections: CardSection[],
  descriptors: ColumnDescriptor[],
): CardSection[] {
  const allowed = visibleColumns(sections, descriptors)
  return sections
    .map((section) => ({
      ...section,
      columns: section.columns.filter((name) => allowed.has(name)),
    }))
    .filter((section) => section.columns.length > 0)
}

/**
 * Начальные значения формы: только показанные поля.
 *
 * Значения полей, которых пользователю не видно, в форму не попадают. Иначе они доехали бы
 * до состояния компонента, а оттуда — в дифф и в тело запроса: скрытое поле, отправленное
 * обратно тем, кто его не видел, — это не «безобидный лишний ключ», а перезапись чужого
 * значения из формы, где его не показывали.
 */
export function initialValues(
  record: EntityRecord | null,
  visible: Map<string, ColumnDescriptor>,
): FormValues {
  const values: FormValues = {}
  if (!record) return values
  for (const [name, descriptor] of visible) {
    values[name] = record.values[name] ?? null
    // Название ссылки едет вместе с её значением: без него поле правки показало бы UUID.
    // Отправке оно не мешает — дифф перебирает только видимые колонки.
    if (descriptor.reference) {
      values[labelKey(name)] = record.values[labelKey(name)] ?? null
    }
  }
  return values
}

/**
 * Поля, которые изменились и потому уходят на сервер.
 *
 * Два фильтра, и оба обязательны:
 *
 * 1. поле должно быть **видимым** — иначе карточка отправит то, чего не показывала;
 * 2. значение должно **отличаться** от исходного — иначе сохранение перезаписывает поля,
 *    которых пользователь не касался, и затирает правку соседа, сделанную параллельно.
 *
 * Сравнение через нормализацию, а не `===`: пустая строка из `<input>` и `null` из базы —
 * одно и то же «не заполнено», и без этого форма считает изменённым каждое пустое поле
 * при первом же открытии.
 */
export function changedFields(
  record: EntityRecord | null,
  values: FormValues,
  visible: Map<string, ColumnDescriptor>,
): FormValues {
  const changes: FormValues = {}
  if (!record) return changes

  for (const name of visible.keys()) {
    const before = normalise(record.values[name])
    const after = normalise(values[name])
    if (before !== after) changes[name] = values[name] === '' ? null : values[name]
  }
  return changes
}

/**
 * Приводит значение к виду, в котором его можно сравнивать.
 *
 * `null`, `undefined` и пустая строка — одно состояние: пользователь, стёрший содержимое
 * поля, и поле, никогда не заполнявшееся, для формы неразличимы, и объявлять это правкой
 * значит слать на сервер запись, которая ничего не меняет.
 */
function normalise(value: unknown): string {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  return String(value)
}

/**
 * Проверяет значения перед отправкой.
 *
 * ## Что эта проверка не делает
 *
 * Не защищает данные. Сервер проверяет то же самое и остаётся единственным, кому можно
 * верить; клиентскую проверку обходит любой, кто откроет консоль. Смысл — сказать про
 * опечатку сразу, а не после отправки формы, которую пользователь успел закрыть.
 *
 * Поэтому правила обязаны совпадать с серверными: расхождение хуже отсутствия проверки —
 * форма показывает «всё в порядке», а сервер отвечает отказом, и непонятно, что править.
 */
export function validate(
  values: FormValues,
  visible: Map<string, ColumnDescriptor>,
): FieldError[] {
  const errors: FieldError[] = []

  for (const [name, descriptor] of visible) {
    const value = values[name]
    if (value === null || value === undefined || value === '') continue
    const text = String(value).trim()

    switch (descriptor.type) {
      case 'number':
        // Number('') равен нулю, а Number('12abc') — NaN: пустоту отсекли выше, здесь
        // остаётся отличить число от того, что на него похоже.
        if (Number.isNaN(Number(text))) {
          errors.push({ field: name, message: 'card.error.number' })
        }
        break

      case 'date':
      case 'timestamp':
        // Date принимает почти всё и молча выдаёт Invalid Date; сравнение с NaN — то, что
        // отличает настоящую дату от строки, похожей на неё.
        if (Number.isNaN(new Date(text).getTime())) {
          errors.push({ field: name, message: 'card.error.date' })
        }
        break

      case 'uuid':
        if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)) {
          errors.push({ field: name, message: 'card.error.uuid' })
        }
        break

      case 'boolean':
        if (text !== 'true' && text !== 'false') {
          errors.push({ field: name, message: 'card.error.boolean' })
        }
        break

      case 'text':
        break
    }
  }

  return errors
}

/**
 * Строит конфликт из того, что пришло с сервера, и того, что пытались записать.
 *
 * Расхождением считается **поле**, а не версия целиком: запись, которую сосед поменял в
 * другом поле, конфликтом не является — иначе всякая активная карточка стала бы
 * неразрешимо конфликтной, и разрешение конфликта разучились бы читать.
 *
 * Сравнивается с тем, что пользователь **правил**, а не со всей формой: поле, которого он
 * не трогал, ему и решать не надо.
 */
export function conflictOf(
  theirs: EntityRecord,
  mine: FormValues,
  base: EntityRecord,
): CardConflict {
  const fields = Object.keys(mine).filter((name) => {
    const theirValue = normalise(theirs.values[name])
    const baseValue = normalise(base.values[name])
    // Трёхстороннее сравнение, как в обмене с 1С (#54): вопрос не «кто новее», а «какая
    // сторона отличается от того, с чего начинали». Поле, которое сервер не менял,
    // конфликтом не является, даже если наше значение от него отличается — в этом и
    // состоит правка.
    return theirValue !== baseValue
  })

  return { theirs, mine, fields }
}

/**
 * Разошлась ли версия.
 *
 * `null` с любой стороны — это **не** «версии совпали». Отсутствие версии значит, что
 * проверить нечего, и прочитать это как «конфликта нет» — тот же способ молча потерять
 * чужую правку, ради недопущения которого блокировка и заводится. Отвечает `false`
 * только там, где обе версии есть и равны.
 */
export function versionsAgree(mine: number | null, theirs: number | null): boolean {
  if (mine === null || theirs === null) return false
  return mine === theirs
}
